import React, { useEffect, useRef } from 'react';
import { animated, useSpring } from '@react-spring/web';
import Appearance from 'styles/Appearance.js';

const ResourceProgressView = ({ message, request, style, title }) => {

    const MIN_DURATION = 1500; // minimum 1.5 seconds
    const PROGRESS_INTERVAL = 50; // update every 50ms for smooth animation
    
    const apiCompletionTimeRef = useRef(null);
    const apiProgressRef = useRef(0);
    const startTimeRef = useRef(null);
    const requestCompletedRef = useRef(false);
    const requestResultRef = useRef(null);

    const [progressBarAnimations, progressBarAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        width: '0%',
    }));

    const runAnimations = async () => {
        
        let completionCallback = null;
        startTimeRef.current = Date.now();

        // run interval to animate progress bar smoothly
        const interval = setInterval(() => {

            const elapsed = Date.now() - startTimeRef.current;
            let newProgress = 0;
            
            // determine progress based on whether request has completed
            if(requestCompletedRef.current === false) {
                newProgress = apiProgressRef.current;
            } else if(elapsed >= MIN_DURATION) {
                newProgress = 100;
            } else {

                // animate from 75% to 100% over remaining time
                const remainingTime = MIN_DURATION - apiCompletionTimeRef.current;
                const timeSinceApiCompleted = elapsed - apiCompletionTimeRef.current;
                const finalProgress = 75 + (timeSinceApiCompleted / remainingTime) * 25;
                newProgress = Math.min(100, finalProgress);
            }

            // apply new progress animation value to animated components
            progressBarAnimationsAPI.start({ width: `${newProgress}%` });

            // determine if we can call completion
            if(requestCompletedRef.current && elapsed >= MIN_DURATION) {

                // animate component to 100% an clear interval
                progressBarAnimationsAPI.start({ width: '100%' });
                clearInterval(interval);

                // notify subscribers that completion has taken place
                if(typeof(completionCallback) === 'function') {
                    completionCallback();
                }
            }

        }, PROGRESS_INTERVAL);

        try {

            // submit request using callback provided by parent, 
            // track actual upload/download progress (0-100), map it to 0-75% range (first 75% is api progress)
            completionCallback = await request(evt => {
                apiProgressRef.current = (evt.progress * 0.75);
            });

            // store result and set completion flag and duration for request
            requestCompletedRef.current = true;
            apiCompletionTimeRef.current = Date.now() - startTimeRef.current;

        } catch(error) {
            clearInterval(interval);
        }
    };

    useEffect(() => {
        runAnimations();
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 125,
            padding: 24,
            textAlign: 'center',
            width: '100%',
            ...style
        }}>
            <span style={{
                ...Appearance.textStyles.layerItemTitle(),
                fontFamily: 'Zen Dots',
                whiteSpace: 'nowrap'
            }}>{title.toUpperCase()}</span>
            <span style={{
                ...Appearance.textStyles.defaultText(),
                marginTop: 6
            }}>{message}</span>
            <div style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.divider(),
                border: `1px solid ${Appearance.colors.softBorder()}`,
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',   
                height: 16,
                padding: 8,
                marginTop: 16,
                width: '100%'
            }}>
                <animated.div style={{
                    backgroundColor: Appearance.colors.primary(),
                    borderRadius: 3,
                    height: 6,
                    ...progressBarAnimations
                }} />
            </div>
        </div>
    )
}
export default ResourceProgressView;